import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Button,
  TextField,
  Box,
  IconButton,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import { Delete, Add } from "@material-ui/icons";
import { Tabs, Tab, Table, TableBody, TableCell, TableHead, TableRow, MenuItem, Chip } from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(4),
    width: "100%",
    background: "#f8fafc",
    minHeight: "100vh",
  },
  card: {
    padding: theme.spacing(3),
    borderRadius: 12,
    marginBottom: theme.spacing(2),
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  },
  row: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "flex-start",
  },
}));

const newPixel = () => ({
  id: `px_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
  name: "",
  pixelId: "",
  accessToken: "",
  pageId: "",
});

const PixelsPage = () => {
  const classes = useStyles();
  const [pixels, setPixels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0);
  const [days, setDays] = useState(30);
  const [report, setReport] = useState({ total: 0, resumo: [], leads: [] });
  const [loadingReport, setLoadingReport] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/meta-pixels");
      setPixels(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Não foi possível carregar os pixels.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadReport = useCallback(async (d) => {
    setLoadingReport(true);
    try {
      const { data } = await api.get(`/meta-pixels/referrals?days=${d}`);
      setReport(data || { total: 0, resumo: [], leads: [] });
    } catch (err) {
      // silencioso
    } finally {
      setLoadingReport(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (tab === 1) loadReport(days);
  }, [tab, days, loadReport]);

  const update = (id, patch) =>
    setPixels((old) => old.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const remove = (id) => {
    if (!window.confirm("Remover este pixel?")) return;
    setPixels((old) => old.filter((p) => p.id !== id));
  };

  const save = async () => {
    for (const p of pixels) {
      if (!p.name || !p.pixelId) {
        toast.error("Preencha o nome e o ID de todos os pixels.");
        return;
      }
    }
    setSaving(true);
    try {
      await api.post("/meta-pixels", { pixels });
      toast.success("Pixels salvos com sucesso.");
      load();
    } catch (err) {
      toast.error("Erro ao salvar os pixels.");
    } finally {
      setSaving(false);
    }
  };

  const testPixel = async (p) => {
    try {
      await api.post(`/meta-pixels/${p.id}/test`, {});
      toast.success("Evento de teste enviado! Confira no Gerenciador de Eventos da Meta.");
    } catch (err) {
      const msg = err?.response?.data?.error || "Falha no teste";
      toast.error(String(msg));
    }
  };

  return (
    <div className={classes.container}>
      <Box style={{ marginBottom: 24 }}>
        <Typography style={{ fontSize: 28, fontWeight: 700, color: "#1a1a2e" }}>
          Pixels do Facebook
        </Typography>
        <Typography style={{ fontSize: 14, color: "#6b7280" }}>
          Cadastre seus pixels aqui e depois selecione no bloco "Pixel da Meta" do fluxo.
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        style={{ marginBottom: 16 }}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="Pixels" />
        <Tab label="Leads por anúncio" />
      </Tabs>

      {tab === 1 && (
        <Paper className={classes.card}>
          <Box style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <TextField
              select
              label="Período"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              size="small"
              variant="outlined"
              style={{ width: 160 }}
            >
              <MenuItem value={7}>Últimos 7 dias</MenuItem>
              <MenuItem value={30}>Últimos 30 dias</MenuItem>
              <MenuItem value={90}>Últimos 90 dias</MenuItem>
            </TextField>
            <Chip label={`${report.total} lead(s) de anúncio`} color="primary" />
            {loadingReport && <CircularProgress size={20} />}
          </Box>

          {report.resumo && report.resumo.length > 0 && (
            <Box style={{ marginBottom: 16 }}>
              <Typography variant="subtitle2" style={{ marginBottom: 6 }}>
                Resumo por anúncio
              </Typography>
              {report.resumo.map((r) => (
                <Box key={r.anuncio} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #f3f4f6" }}>
                  <span style={{ fontSize: 13 }}>{r.titulo || r.anuncio}</span>
                  <b style={{ fontSize: 13 }}>{r.total}</b>
                </Box>
              ))}
            </Box>
          )}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Contato</TableCell>
                <TableCell>Anúncio</TableCell>
                <TableCell>ID do anúncio</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(report.leads || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} style={{ color: "#9ca3af" }}>
                    Nenhum lead de anúncio ainda. Assim que alguém chegar por um
                    anúncio Click-to-WhatsApp, aparece aqui.
                  </TableCell>
                </TableRow>
              )}
              {(report.leads || []).map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{new Date(l.createdAt).toLocaleString("pt-BR")}</TableCell>
                  <TableCell>
                    {l.contactName || "-"}
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>{l.contactNumber}</div>
                  </TableCell>
                  <TableCell>{l.headline || "-"}</TableCell>
                  <TableCell style={{ fontSize: 11 }}>{l.sourceId || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {tab === 0 && (loading ? (
        <CircularProgress />
      ) : (
        <>
          {pixels.length === 0 && (
            <Paper className={classes.card}>
              <Typography variant="body2" style={{ color: "#6b7280" }}>
                Nenhum pixel cadastrado ainda. Clique em "Adicionar pixel".
              </Typography>
            </Paper>
          )}

          {pixels.map((p) => (
            <Paper key={p.id} className={classes.card}>
              <Box className={classes.row}>
                <TextField
                  label="Nome"
                  value={p.name || ""}
                  onChange={(e) => update(p.id, { name: e.target.value })}
                  size="small"
                  variant="outlined"
                  style={{ flex: 1, minWidth: 200 }}
                />
                <TextField
                  label="ID do Pixel"
                  value={p.pixelId || ""}
                  onChange={(e) => update(p.id, { pixelId: e.target.value })}
                  size="small"
                  variant="outlined"
                  style={{ flex: 1, minWidth: 200 }}
                />
                <IconButton onClick={() => remove(p.id)} size="small">
                  <Delete style={{ color: "#dc2626" }} />
                </IconButton>
              </Box>
              <Box className={classes.row} style={{ marginTop: 12 }}>
                <TextField
                  label="Token de acesso"
                  value={p.accessToken || ""}
                  onChange={(e) => update(p.id, { accessToken: e.target.value })}
                  size="small"
                  variant="outlined"
                  type="password"
                  style={{ flex: 2, minWidth: 260 }}
                  helperText={
                    p.hasToken
                      ? "Token já salvo. Preencha só se quiser trocar."
                      : "Gere em Gerenciador de Eventos → Configurações → API de Conversões"
                  }
                />
                <TextField
                  label="Page ID"
                  value={p.pageId || ""}
                  onChange={(e) => update(p.id, { pageId: e.target.value })}
                  size="small"
                  variant="outlined"
                  style={{ flex: 1, minWidth: 200 }}
                  helperText="Página vinculada ao WhatsApp Business"
                />
                <Button size="small" onClick={() => testPixel(p)} style={{ marginTop: 4 }}>
                  Enviar teste
                </Button>
              </Box>
            </Paper>
          ))}

          <Box style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <Button
              startIcon={<Add />}
              variant="outlined"
              onClick={() => setPixels((old) => [...old, newPixel()])}
            >
              Adicionar pixel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={save}
              disabled={saving}
            >
              {saving ? <CircularProgress size={20} /> : "Salvar"}
            </Button>
          </Box>
        </>
      ))}
    </div>
  );
};

export default PixelsPage;
